import { Component, OnInit, Input, Output, EventEmitter, Self, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { DepartmentService } from 'src/app/services/department.service';
import { NgControl, AbstractControl, UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';


@Component({
  selector: 'multiselect-department',
  templateUrl: './multiselect-department.component.html',
  styleUrls: ['./multiselect-department.component.scss']
})
export class MultiselectDepartmentComponent implements OnInit, OnDestroy {

  @Input() fieldError: string;
  @Input() label: string = 'Departments';
  @Input() required: boolean;
  @Input() isSubmitted: boolean;
  @Input() defaultValueLabel: string = 'All'
  @Input() disable: boolean = false;

  @Output() ready: EventEmitter<any> = new EventEmitter();

  @ViewChild('multiselect') container

  // params
  animate = false;
  isOpen = false;

  // options
  options: any;

  loading = true;

  form: UntypedFormGroup = this._fb.group({
    inputCtrl: [this.defaultValueLabel],
    selectAllCtrl: [true],
    optionsCtrls: this._fb.array([])
  });

  public value: any;

  numSelected: string = 'All';

  // subscriptions
  private _departmentSub: Subscription;

  constructor(
    private _departmentSvc: DepartmentService,
    private _fb: UntypedFormBuilder,
    @Self() public ngCtrl: NgControl
  ) {
    ngCtrl.valueAccessor = this;
  }
  
  ngOnInit(): void {

    this.form.controls.inputCtrl.setValue(this.defaultValueLabel);
    this.numSelected = this.defaultValueLabel

    this._departmentSvc.getDepartments();
    this._departmentSub = this._departmentSvc.Departments.subscribe((res: any) => {
      if (res) {
        this.options = res;
        this.loading = false;
        this.populateControls();
        this.setValueChangeEvents();
        this.ready.emit();
      }
    });
  
  }

  // called everytime an input changes
  ngOnChanges(changes: SimpleChanges): void {

    // Populate controls once options are finished loading
    if (changes.loading && changes.loading.previousValue && !changes.loading.currentValue) {
      this.populateControls();
      this.setValueChangeEvents();
    }

    // Conditionally enable/disable inputCtrl based on inputs
    if (!this.disable && !this.loading) {
      this.form.controls.inputCtrl.disabled && this.form.controls.inputCtrl.enable();
    }
    else {
      !this.form.controls.inputCtrl.disabled && this.form.controls.inputCtrl.disable();
    }
  }

  ngOnDestroy(): void {
    if (this._departmentSub) {
      this._departmentSub.unsubscribe();
    }
  }

  populateControls(): void{

    for (let index = 0; index < this.options.length; index++) {
      this.addItemControl();
    }
  }

  addItemControl(): void {
    const control = new UntypedFormGroup({
      itemCtrl: new UntypedFormControl(false)
    });
    
    (<UntypedFormArray>this.form.get('optionsCtrls')).push(control);
  }

  getSelectionAmount(): number {
    
    const optionsCtrls = this.form.get('optionsCtrls') as UntypedFormArray;
    const selectAllCtrl = this.form.get('selectAllCtrl');

    if (selectAllCtrl.value) {
      return optionsCtrls.length;
    }
    else {
      let selectedCount: number = 0;
      for (let i = 0; i < optionsCtrls.length; i++) {
        const itemCtrl = optionsCtrls.at(i).get('itemCtrl');
        if (itemCtrl.value) {
          selectedCount++;
        }
      }
      return selectedCount;
    }
  }

  updateSelectedRecords(): void {
    const optionsCtrls = this.form.get('optionsCtrls') as UntypedFormArray;
    const selectAllCtrl = this.form.get('selectAllCtrl');

    if (selectAllCtrl.value) {
      this.onValueChange(this.defaultValueLabel);
    }
    else {
      let outputRecords = [];
      for (let i = 0; i < optionsCtrls.length; i++) {
        const itemCtrl = optionsCtrls.at(i).get('itemCtrl');
        if (itemCtrl.value) {
          outputRecords.push(this.options[i].Id);
        }
      }
      this.onValueChange(outputRecords);
    }

    this.numSelected = selectAllCtrl.value ? this.defaultValueLabel : `${this.getSelectionAmount()} selected`
  }
  
  /**
   * Open menu event handler
   */
  openMenu(): void {

    // dont open if field is disabled
    if (this.ngCtrl.disabled) return;
    if(this.animate == true && this.isOpen) return;
    
    setTimeout(() => {

      this.isOpen = true;
      setTimeout(() => {
        this.animate = true;
      }, 100);

    }, 100);
    

  }

  //Only close if the target of the event is not a child of this components wrapper div tag
  attemptCloseMenu(e: any){

    if(this.container && e.target && (e.target === Window || !this.container.nativeElement.contains(e.target))){
      this.closeMenu();
    }
  }

  /**
   * Close menu event handler
   */
  closeMenu(): void {
    

    this.animate = false;
    this.isOpen = false;
  }

  /**
   * Closes the menu if tab key is pressed
   * @param e
   */
  onKeyup(e: any): void {

    if (e.keyCode === 9) {
      this.closeMenu();
    }

  }

  setValueChangeEvents(): void {

    // Select All Control
    this.form.controls.selectAllCtrl.valueChanges
    .subscribe((checked) => {

      this.updateSelectedRecords();

      if (checked) {
        this.toggleOptionItems(false);
      }
      else {
        if (this.getSelectionAmount() === 0) {
          selectAllCtrl.setValue(true);
        }
      }
    })

    // Items Controls
    const optionsCtrls = this.form.get('optionsCtrls') as UntypedFormArray;
    const selectAllCtrl = this.form.get('selectAllCtrl');

    for (let i = 0; i < optionsCtrls.length; i++) {
      const itemCtrl = optionsCtrls.at(i).get('itemCtrl');
      itemCtrl.valueChanges.subscribe((value) => {

        this.updateSelectedRecords();

        if (value && selectAllCtrl.value) {
          selectAllCtrl.setValue(false);
        }

        if (this.getSelectionAmount() === 0) {
          selectAllCtrl.setValue(true);
        }

      })
    }
  }

  toggleOptionItems(checked: boolean) {
    
    const optionsCtrls = this.form.get('optionsCtrls') as UntypedFormArray;

    for (let i = 0; i < optionsCtrls.length; i++) {
      const itemCtrl = optionsCtrls.at(i).get('itemCtrl');
      itemCtrl.setValue(checked);
    }
  }

  onValueChange(value: Array<any> | string) {
    this.value = value;
    this.onChange(this.value);
  }

  // control value accessor params

  onChange(_value: any) {}

  onTouched() {}

  writeValue(_value: any) {

  }

  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  validate(_ctrl: AbstractControl) {
    // return Validators.required(ctrl);
  }

}
